import { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { IoRefresh, IoFlash, IoTrendingUp, IoWallet } from "react-icons/io5";

import GlassCard from "components/GlassCard";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import VuiButton from "components/ui/VuiButton";
import PorterTable from "components/PorterTable";

import { adminService } from "services/adminService";

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatTokens(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatCost(usd) {
  if (usd == null) return "—";
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(3)}`;
}

function friendlyAnalyzerName(raw) {
  return raw
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// ── Stat Card ─────────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <GlassCard sx={{ height: "100%" }}>
      <VuiBox display="flex" flexDirection="column" gap={1} p={0.5}>
        <VuiBox display="flex" alignItems="center" gap={1}>
          <VuiBox sx={{ color: "rgba(255,255,255,0.6)", display: "flex" }}>
            <Icon size="18px" />
          </VuiBox>
          <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.75rem" }}>
            {label}
          </VuiTypography>
        </VuiBox>
        <VuiTypography variant="h4" color="white" fontWeight="bold">
          {value}
        </VuiTypography>
        {sub && (
          <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.7rem" }}>
            {sub}
          </VuiTypography>
        )}
      </VuiBox>
    </GlassCard>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function TokenUsage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snapshotting, setSnapshotting] = useState(false);
  const [snapshotResult, setSnapshotResult] = useState(null);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminService.getTokenUsage(days);
      setData(res.data);
    } catch (e) {
      setError("Failed to load token usage data.");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSnapshot = async () => {
    setSnapshotting(true);
    setSnapshotResult(null);
    try {
      await adminService.triggerTokenSnapshot();
      setSnapshotResult("Snapshot triggered — refreshing...");
      setTimeout(fetchData, 1500);
    } catch {
      setSnapshotResult("Snapshot failed.");
    } finally {
      setSnapshotting(false);
    }
  };

  // ── Derived values ───────────────────────────────────────────────────────
  const avgDaily =
    data && data.dailySnapshots?.length > 0
      ? Math.round(data.totalTokens / data.dailySnapshots.length)
      : null;

  // ── Analyzer type table ──────────────────────────────────────────────────
  const analyzerColumns = [
    { name: "Analyzer", align: "left" },
    { name: "Calls", align: "center" },
    { name: "Input Tokens", align: "center" },
    { name: "Output Tokens", align: "center" },
    { name: "Total Tokens", align: "center" },
    { name: "Est. Cost", align: "center" },
  ];

  const analyzerRows = (data?.byAnalyzerType ?? []).map((a) => ({
    Analyzer: (
      <VuiTypography variant="caption" color="white" fontWeight="medium">
        {friendlyAnalyzerName(a.analyzerType)}
      </VuiTypography>
    ),
    Calls: (
      <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
        {a.callCount}
      </VuiTypography>
    ),
    "Input Tokens": (
      <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
        {formatTokens(a.promptTokens)}
      </VuiTypography>
    ),
    "Output Tokens": (
      <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
        {formatTokens(a.completionTokens)}
      </VuiTypography>
    ),
    "Total Tokens": (
      <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
        {formatTokens(a.totalTokens)}
      </VuiTypography>
    ),
    "Est. Cost": (
      <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
        {formatCost(a.estimatedCostUsd)}
      </VuiTypography>
    ),
  }));

  // ── Daily trend table ────────────────────────────────────────────────────
  const dailyColumns = [
    { name: "Date", align: "left" },
    { name: "Input Tokens", align: "center" },
    { name: "Output Tokens", align: "center" },
    { name: "Total Tokens", align: "center" },
    { name: "Est. Cost", align: "center" },
  ];

  const dailyRows = (data?.dailySnapshots ?? []).map((d) => ({
    Date: (
      <VuiTypography variant="caption" color="white">
        {new Date(d.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
      </VuiTypography>
    ),
    "Input Tokens": (
      <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
        {formatTokens(d.promptTokens)}
      </VuiTypography>
    ),
    "Output Tokens": (
      <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
        {formatTokens(d.completionTokens)}
      </VuiTypography>
    ),
    "Total Tokens": (
      <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
        {formatTokens(d.totalTokens)}
      </VuiTypography>
    ),
    "Est. Cost": (
      <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
        {formatCost(d.estimatedCostUsd)}
      </VuiTypography>
    ),
  }));

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <VuiBox display="flex" flexDirection="column" gap={3}>

      {/* Header row */}
      <VuiBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <VuiBox display="flex" alignItems="center" gap={1}>
          <VuiTypography variant="h6" color="white">
            LLM Token Usage
          </VuiTypography>
          {/* Period selector */}
          {[7, 30, 90].map((d) => (
            <VuiButton
              key={d}
              size="small"
              variant={days === d ? "contained" : "outlined"}
              color="info"
              onClick={() => setDays(d)}
              sx={{ minWidth: "48px", px: 1, py: 0.25, fontSize: "0.72rem" }}
            >
              {d}d
            </VuiButton>
          ))}
        </VuiBox>

        <VuiBox display="flex" alignItems="center" gap={1}>
          {snapshotResult && (
            <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.72rem" }}>
              {snapshotResult}
            </VuiTypography>
          )}
          <VuiButton
            size="small"
            variant="outlined"
            color="info"
            onClick={handleSnapshot}
            disabled={snapshotting}
            sx={{ minHeight: "32px" }}
          >
            {snapshotting ? <CircularProgress size={12} /> : "Sync Now"}
          </VuiButton>
          <VuiButton
            size="small"
            variant="outlined"
            color="secondary"
            onClick={fetchData}
            disabled={loading}
            sx={{ minWidth: "36px", minHeight: "32px", px: 1 }}
          >
            <IoRefresh size="14px" />
          </VuiButton>
        </VuiBox>
      </VuiBox>

      {/* Error */}
      {error && (
        <VuiTypography variant="caption" sx={{ color: "#f87171" }}>
          {error}
        </VuiTypography>
      )}

      {/* Stat cards */}
      {loading ? (
        <VuiBox display="flex" justifyContent="center" py={4}>
          <CircularProgress sx={{ color: "rgba(255,255,255,0.4)" }} />
        </VuiBox>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon={IoFlash}
                label={`Total Tokens (${days}d)`}
                value={formatTokens(data?.totalTokens ?? 0)}
                sub={`${formatTokens(data?.totalPromptTokens ?? 0)} in · ${formatTokens(data?.totalCompletionTokens ?? 0)} out`}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon={IoWallet}
                label={`Estimated Cost (${days}d)`}
                value={formatCost(data?.totalEstimatedCostUsd ?? 0)}
                sub="gemini-2.5-flash-lite · $0.10/1M in · $0.40/1M out"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon={IoTrendingUp}
                label="Avg Daily Tokens"
                value={avgDaily != null ? formatTokens(avgDaily) : "—"}
                sub={`over ${data?.dailySnapshots?.length ?? 0} active days`}
              />
            </Grid>
          </Grid>

          {/* By analyzer type */}
          {analyzerRows.length > 0 ? (
            <GlassCard>
              <VuiBox p={1}>
                <VuiTypography variant="button" color="white" fontWeight="medium" mb={1.5}>
                  By Analyzer Type
                </VuiTypography>
                <PorterTable columns={analyzerColumns} rows={analyzerRows} />
              </VuiBox>
            </GlassCard>
          ) : (
            <GlassCard>
              <VuiBox p={2} display="flex" justifyContent="center">
                <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>
                  No usage data for this period. Trigger a scrape run then click "Sync Now".
                </VuiTypography>
              </VuiBox>
            </GlassCard>
          )}

          {/* Daily trend */}
          {dailyRows.length > 0 && (
            <GlassCard>
              <VuiBox p={1}>
                <VuiTypography variant="button" color="white" fontWeight="medium" mb={1.5}>
                  Daily Breakdown
                </VuiTypography>
                <PorterTable columns={dailyColumns} rows={dailyRows} />
              </VuiBox>
            </GlassCard>
          )}
        </>
      )}
    </VuiBox>
  );
}
